import { Box, Button, FormControl, FormLabel, Grid, Heading, HStack, Icon, Stack, Switch, Text } from '@chakra-ui/react';
import { Field, LooseObject, ServiceRecord } from 'api/types';
import { get, isBoolean, isNil } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdTireRepair } from 'react-icons/md';
import {
  RiBrushFill,
  RiCarFill,
  RiCheckboxCircleFill,
  RiDashboard3Fill,
  RiFileListFill,
  RiFireFill,
  RiListCheck,
  RiSettings3Fill,
  RiToolsFill,
} from 'react-icons/ri';
import { NavLink } from 'react-router-dom';
import { useCopyToClipboard } from 'usehooks-ts';

import InputCopyField from '../../../components/form-elements/InputCopyField';
import { ServiceRecordIcon } from '../../../components/Icons';
import Link from '../../../components/Link';
import { Warning } from '../../../components/Warning';
import { dateTimeTransformer, dateTransformerNoTimezone } from '../../../helpers/dateTransformer';
import { displayRow, renderValue } from '../../../helpers/general';
import { ContentCard } from '../../../layout/ContentCard';
import { LanguageHighlight } from '../LanguageHighlight';

// OPS view for the Mazda Germany DSB form. Its sections follow the manufacturer's paper service
// booklet rather than the generic form groups, so it renders schema.op_sections (defined in the
// backend's dsb_form.op_sections) instead of going through ServiceRecordData. The backend owns
// both the section order and the field order; this component only renders what it is handed, so
// adding or reordering fields is a backend-only change.
type SectionEntry = string | { field: string; label_index?: number; all_labels?: boolean } | { label: string; fields: SectionEntry[] };

type OpSection = { label: string; fields: SectionEntry[] };

// The generic form groups ship an icon URL from the backend, but these paper-booklet sections have
// none, so they get icons here by position.
const SECTION_ICONS = [
  RiToolsFill, // Neue Wartung
  RiSettings3Fill, // Allgemeine Optionen
  RiCarFill, // Fahrzeug Innen
  RiBrushFill, // Außen am Fahrzeug
  RiFireFill, // Unter der Haube
  RiDashboard3Fill, // Fahrzeugunterseite
  MdTireRepair, // Reifen prüfen
  RiCheckboxCircleFill, // Abschließende Arbeiten
  RiFileListFill, // Fahrzeugstatusbericht über MCVV abfragen
  RiListCheck, // Weitere Felder (fallback)
];

const flattenSchemaFields = (schema: any): Field[] => {
  const groups = get(schema, 'sections[0].groups', []);
  const fields: Field[] = [];
  const collect = (list: Field[]) => {
    list.forEach((field) => {
      fields.push(field);
      if (field.children?.length) {
        collect(field.children);
      }
    });
  };
  groups.forEach((group: any) => collect(group.fields || []));
  return fields;
};

const MazdaGermany = (props: {
  record: ServiceRecord;
  isLoading: boolean;
  schema: any;
  workshopLanguage: string;
  canChangeVin?: boolean;
  onMazdaWlanConnectedChange?: (value: boolean) => void;
}) => {
  const [applyAnnimation, setApplyAnnimation] = useState(false);
  const { t } = useTranslation();
  const { isLoading, record, schema, workshopLanguage, canChangeVin, onMazdaWlanConnectedChange } = props;

  const [value, copy] = useCopyToClipboard();
  const recordData: LooseObject = record?.data || {};

  const additionalNotes = get(record, 'additional_notes', '-');

  const showMazdaWlanSection = record.mazda_has_wlan_connected != null || record.mazda_wlan_consent != null;
  const workshopGaveWlanConsent = record.mazda_wlan_consent === true;
  const isInProgress = record.status === 200;

  const [hasWlanConnected, setHasWlanConnected] = useState(() => Boolean(record.mazda_wlan_consent));
  useEffect(() => {
    setHasWlanConnected(Boolean(record.mazda_wlan_consent));
  }, [record.id, record.mazda_wlan_consent]);
  useEffect(() => {
    onMazdaWlanConnectedChange?.(hasWlanConnected);
  }, [hasWlanConnected, onMazdaWlanConnectedChange]);

  const opSections: OpSection[] = get(schema, 'op_sections', []);
  const fieldsByName = Object.fromEntries(flattenSchemaFields(schema).map((field) => [field.name, field]));

  const renderField = (slug: string, labelIndex?: number, allLabels?: boolean, key?: string) => {
    const field = fieldsByName[slug];
    if (!field) return null;

    const fieldValue = recordData[slug];
    const contentOnNewLine = field?.display?.type === 'string';
    const hasValue = !isNil(fieldValue) && fieldValue !== '';

    // one row per label: for fields printed as several separate line items on the paper form
    if (allLabels && field.labels?.length) {
      if (fieldValue === false) return null;
      return (
        <Fragment key={key}>
          {field.labels.map((label, idx) => (
            <Fragment key={`${slug}-${idx}`}>{displayRow({ label, content: '', isLoading, noColon: isBoolean(fieldValue) })}</Fragment>
          ))}
        </Fragment>
      );
    }

    const label = labelIndex !== undefined ? (field.labels?.[labelIndex] ?? field.label) : field.label;

    if (!hasValue) {
      if (!field?.display?.display_always) return null;
      return <Fragment key={key}>{displayRow({ label, content: t('common:no'), isLoading, contentOnNewLine })}</Fragment>;
    }

    return fieldValue !== false ? (
      <Fragment key={key}>
        {displayRow({
          label,
          content: fieldValue === true ? '' : renderValue(fieldValue, field),
          isLoading,
          contentOnNewLine,
          noColon: isBoolean(fieldValue),
        })}
      </Fragment>
    ) : null;
  };

  const renderEntry = (entry: SectionEntry, index: number) => {
    if (typeof entry === 'string') {
      return renderField(entry, undefined, undefined, `${entry}-${index}`);
    }

    // sub-heading with indented fields (e.g. the wheel-corner readings)
    if ('fields' in entry) {
      const rows = entry.fields.map(renderEntry).filter(Boolean);
      if (!rows.length) return null;
      return (
        <Stack key={`group-${index}`} spacing={1}>
          {displayRow({ label: entry.label, content: null, isLoading })}
          <Stack pl={6} spacing={1}>
            {rows}
          </Stack>
        </Stack>
      );
    }

    return renderField(entry.field, entry.label_index, entry.all_labels, `${entry.field}-${index}`);
  };

  return (
    <ContentCard
      isLoading={isLoading}
      icon={<ServiceRecordIcon boxSize={10} />}
      minH="fit-content"
      header={`${t('service_record:header')}( ${record?.user_readable_id} )`}
      data-test-id="service-record-detail-card"
      contentDescription={
        <HStack justifyContent={'space-between'}>
          <Link to={`/app/op/workshops/${record?.workshop_id}`}>{t('service_record:workshops.title')}</Link>
          <Text>
            {t('forms:submitted_at.label', {
              date: dateTimeTransformer(get(record, 'submitted_at', '-')),
            })}
          </Text>
        </HStack>
      }
    >
      {showMazdaWlanSection && (
        <Stack spacing={3} mb={6}>
          <Warning bgColor={workshopGaveWlanConsent ? 'green.100' : 'orange.100'} data-test-id="mazda-wlan-consent-status">
            <Stack spacing={1}>
              <Text fontWeight="700">
                {workshopGaveWlanConsent
                  ? t('service_record:mazda_wlan_connected.consent_given.title')
                  : t('service_record:mazda_wlan_connected.consent_missing.title')}
              </Text>
              <Text>
                {workshopGaveWlanConsent
                  ? t('service_record:mazda_wlan_connected.consent_given.description')
                  : t('service_record:mazda_wlan_connected.consent_missing.description')}
              </Text>
            </Stack>
          </Warning>

          <FormControl display="flex" alignItems="center" mt={2} data-test-id="mazda-wlan-connected-toggle">
            <HStack spacing={3}>
              <FormLabel mb="0" fontWeight="700">
                {t('service_record:mazda_wlan_connected.toggle_label')}
              </FormLabel>
              <Switch size="lg" isChecked={hasWlanConnected} isDisabled={!isInProgress} onChange={(e) => setHasWlanConnected(e.target.checked)} />
            </HStack>
          </FormControl>
        </Stack>
      )}

      <Grid w="full" templateColumns="repeat(auto-fit, minmax(340px, 1fr))" gap={4} rowGap={8}>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Heading size={'xxs'}>{t('service_record:title')}</Heading>
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: dateTransformerNoTimezone(get(record, 'date', '-'), 'yyyy-MM-dd'),
              }}
              field={{
                value: dateTimeTransformer(get(record, 'date', '-')),
                label: t('forms:service_date.label'),
              }}
              aria-label={'service_date'}
            />
            <Box onFocus={() => setApplyAnnimation(true)}>
              <InputCopyField
                copy={{
                  currentCopiedValue: value,
                  handleCopy: copy,
                }}
                field={{
                  value: get(record?.vehicle, 'vin', '-'),
                  label: t('forms:vin.label'),
                }}
                customContent={
                  canChangeVin &&
                  ((record?.status === 500 && record?.status_reason === 501) || (record?.status === 500 && record?.status_reason === 599)) && (
                    <Button colorScheme={'red'} size="sm" as={NavLink} to={`/app/op/service-record/${record?.id}/vin-update`}>
                      {t('service_record:vin.update.button')}
                    </Button>
                  )
                }
                aria-label={'vin'}
              />
            </Box>
            {workshopLanguage?.toLocaleLowerCase() !== 'de' && (
              <LanguageHighlight workshopLanguage={workshopLanguage} applyAnnimation={applyAnnimation} />
            )}
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: get(record, 'mileage', '-'),
              }}
              field={{
                value: get(record, 'mileage', '-') + ' km',
                label: t('forms:mileage.label'),
              }}
              aria-label={'mileage'}
            />
          </Stack>
          <Stack spacing={2}>
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
              }}
              field={{
                value: get(record, 'workshop_reference', '-'),
                label: t('forms:workshop_reference.label.default'),
              }}
              aria-label={'workshop_reference'}
            />
          </Stack>
        </Stack>
        <Grid gap={4} alignSelf={'start'}>
          <Heading size={'xxs'}>{t('service_record:data')}</Heading>
          {opSections.map((section, sectionIndex) => {
            const rows = section.fields.map(renderEntry).filter(Boolean);
            if (!rows.length) return null;
            const sectionIcon = SECTION_ICONS[sectionIndex % SECTION_ICONS.length];
            return (
              <Box key={`${section.label}-${sectionIndex}`}>
                <Stack direction={'row'} alignItems={'center'} spacing={3}>
                  <Icon as={sectionIcon} ml={2} boxSize={8} color={'blue.500'} />
                  <Heading fontSize="xxs" lineHeight={1}>
                    {section.label}
                  </Heading>
                </Stack>
                <Stack spacing={2} mt={3} ml="60px">
                  {rows}
                </Stack>
              </Box>
            );
          })}
          {!isNil(additionalNotes) && (
            <Box ml="60px">
              {displayRow({
                label: t('forms:additional_notes.label'),
                content: additionalNotes,
                isLoading,
                contentOnNewLine: true,
              })}
            </Box>
          )}
        </Grid>
      </Grid>
    </ContentCard>
  );
};

export default MazdaGermany;
